import { Link } from "@tanstack/react-router";
import { Topbar } from "../Topbar";
import { Footer } from "../Footer";
import { Hero } from "../Hero";
import { CTABlock } from "../CTABlock";
import { ReadingProgress } from "../blog/ReadingProgress";
import { BlogTOC } from "../blog/BlogTOC";
import { pathMap, SITE_URL, type Locale } from "@/lib/locale";
import {
  GLOSSARY_CATEGORY_LABELS,
  getGlossaryEntryById,
  type GlossaryEntry,
} from "@/lib/glossary";
import { formatGlossaryDate, type GlossaryDetail } from "@/lib/glossary-content";
import { services } from "@/lib/services-registry";

type Props = {
  entry: GlossaryEntry;
  detail: GlossaryDetail;
  locale: Locale;
};

export function GlossaryDetailPage({ entry, detail, locale }: Props) {
  const isEn = locale === "en";
  const overviewPath = isEn ? pathMap.glossary.en : pathMap.glossary.de;
  const canonical = `${SITE_URL}${overviewPath}/${entry.slug[locale]}`;
  const tocLabel = isEn ? "On this page" : "Inhaltsverzeichnis";

  const tr = isEn
    ? {
        eyebrow: "Glossary",
        breadHome: "Start",
        breadGlossary: "Glossary",
        updated: "Updated",
        readingTime: "min read",
        relatedHead: "Related terms",
        deepenHead: "Deepen",
        deepenLeadServices: "Operationally executed in our services:",
        deepenLeadBlog: "Strategic context in the blog:",
        backAll: "All entries",
        ctaEyebrow: "Mandate-grade conversation",
        ctaHead: "Want substance on this in your mandate?",
        ctaBody: "If you're looking for substance on a specific question — let's talk. We answer with the same clarity we write with.",
        ctaPrimary: "Start a conversation",
        ctaSecondary: "All services",
      }
    : {
        eyebrow: "Glossar",
        breadHome: "Start",
        breadGlossary: "Glossar",
        updated: "Stand",
        readingTime: "Min Lesezeit",
        relatedHead: "Verwandte Begriffe",
        deepenHead: "Vertiefung",
        deepenLeadServices: "Operativ umgesetzt in unseren Leistungen:",
        deepenLeadBlog: "Strategische Einordnung im Blog:",
        backAll: "Alle Einträge",
        ctaEyebrow: "Mandate-Gespräch",
        ctaHead: "Substanz statt Generika in Ihrem Mandat?",
        ctaBody: "Wenn Sie zu einer konkreten Frage Substanz suchen — sprechen wir. Wir antworten mit der gleichen Klarheit, mit der wir schreiben.",
        ctaPrimary: "Erst-Gespräch anfragen",
        ctaSecondary: "Alle Leistungen",
      };

  const metaStrip = [
    GLOSSARY_CATEGORY_LABELS[entry.category][locale],
    `${tr.updated} ${formatGlossaryDate(entry.updatedAt, locale)}`,
    detail.fm.readingTimeMinutes ? `${detail.fm.readingTimeMinutes} ${tr.readingTime}` : null,
  ].filter((v): v is string => Boolean(v));

  const related = (entry.relatedTerms ?? [])
    .map(getGlossaryEntryById)
    .filter((e): e is GlossaryEntry => Boolean(e));

  return (
    <>
      <ReadingProgress />
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow={tr.eyebrow}
          headline={entry.term[locale]}
          lead={entry.shortDefinition[locale]}
          metaStrip={metaStrip}
          breadcrumbs={[
            { label: tr.breadHome, to: isEn ? "/en" : "/" },
            { label: tr.breadGlossary, to: overviewPath },
            { label: entry.term[locale] },
          ]}
        />

        <section className="bg-white py-20">
          <div className="mx-auto max-w-[1200px] px-6 md:px-12">
            <div className="grid gap-10 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-16">
              <BlogTOC headings={detail.headings} label={tocLabel} />
              <div className="min-w-0">
                <article
                  className="blog-prose"
                  dangerouslySetInnerHTML={{ __html: detail.bodyHtml }}
                />

                {related.length > 0 && (
                  <div className="mt-16">
                    <h2 className="mb-5 font-mono text-xs uppercase tracking-[0.18em] text-ink-4">
                      {tr.relatedHead}
                    </h2>
                    <ul className="grid gap-3 md:grid-cols-2">
                      {related.map((r) => {
                        const href = r.tier === 1
                          ? `${overviewPath}/${r.slug[locale]}`
                          : `${overviewPath}#${r.id}`;
                        return (
                          <li key={r.id}>
                            <Link
                              to={href}
                              className="group block rounded-md border border-rule bg-white p-4 transition-colors hover:border-cyan-deep/40"
                            >
                              <div className="font-display text-base text-ink group-hover:text-cyan-deep">
                                {r.term[locale]}
                              </div>
                              <div className="mt-1 line-clamp-2 text-sm text-ink-3">
                                {r.shortDefinition[locale]}
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                <DeepenBlock entry={entry} locale={locale} tr={tr} />

                <div className="mt-20">
                  <CTABlock
                    headline={
                      <>
                        <span className="block eyebrow mb-3 normal-case tracking-[0.18em]">
                          {tr.ctaEyebrow}
                        </span>
                        <em>{tr.ctaHead}</em>
                      </>
                    }
                    body={tr.ctaBody}
                    primary={{ label: tr.ctaPrimary, to: isEn ? pathMap.contact.en : pathMap.contact.de }}
                    secondary={{ label: tr.ctaSecondary, to: isEn ? pathMap.services.en : pathMap.services.de }}
                  />
                </div>

                <div className="mt-16 flex items-center justify-between border-t border-rule pt-6 font-mono text-xs uppercase tracking-wider text-ink-4">
                  <Link to={overviewPath} className="hover:text-cyan-deep">
                    ← {tr.backAll}
                  </Link>
                  <span>
                    {tr.updated}: {formatGlossaryDate(entry.updatedAt, locale)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* JSON-LD: DefinedTerm */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            "@id": canonical,
            name: entry.term[locale],
            description: entry.shortDefinition[locale],
            termCode: entry.id,
            inDefinedTermSet: `${SITE_URL}${overviewPath}`,
            url: canonical,
            inLanguage: isEn ? "en-US" : "de-DE",
          }),
        }}
      />

      {/* JSON-LD: BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: tr.breadHome, item: `${SITE_URL}${isEn ? "/en" : "/"}` },
              { "@type": "ListItem", position: 2, name: tr.breadGlossary, item: `${SITE_URL}${overviewPath}` },
              { "@type": "ListItem", position: 3, name: entry.term[locale], item: canonical },
            ],
          }),
        }}
      />
    </>
  );
}

function DeepenBlock({
  entry,
  locale,
  tr,
}: {
  entry: GlossaryEntry;
  locale: Locale;
  tr: { deepenHead: string; deepenLeadServices: string; deepenLeadBlog: string };
}) {
  const svcLinks = (entry.crossLinks?.services ?? [])
    .map((k) => {
      const path = pathMap[k]?.[locale];
      if (!path) return null;
      const id = k.startsWith("service:") ? k.slice("service:".length) : k;
      const reg = services[id];
      const label = reg ? reg[locale].name : path;
      return { key: k, path, label };
    })
    .filter((x): x is { key: string; path: string; label: string } => Boolean(x));
  const blogs = (entry.crossLinks?.blogPosts ?? [])
    .map((k) => ({ key: k, path: pathMap[k]?.[locale] }))
    .filter((x): x is { key: string; path: string } => Boolean(x.path));

  if (svcLinks.length === 0 && blogs.length === 0) return null;

  return (
    <div className="mt-16 rounded-lg border border-rule bg-bg-subtle p-7">
      <h2 className="mb-4 font-mono text-xs uppercase tracking-[0.18em] text-ink-4">
        {tr.deepenHead}
      </h2>
      {blogs.length > 0 && (
        <div className="mb-3">
          <p className="mb-2 text-[15px] text-ink-3">{tr.deepenLeadBlog}</p>
          <ul className="space-y-1.5">
            {blogs.map((b) => (
              <li key={b.key}>
                <Link to={b.path} className="text-cyan-deep hover:underline">
                  → {b.path}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {svcLinks.length > 0 && (
        <div>
          <p className="mb-2 text-[15px] text-ink-3">{tr.deepenLeadServices}</p>
          <ul className="space-y-1.5">
            {svcLinks.map((s) => (
              <li key={s.key}>
                <Link to={s.path} className="text-cyan-deep hover:underline">
                  → {s.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
